'use client';

import React, { useId } from 'react';
import { motion } from 'motion/react';
import twMerge from 'utilities/src/twMerge';
import IconComponent from '../Icon';
import AiWidgetPanel from './_Panel';
import WidgetEntranceEffect from './_WidgetEntranceEffect';
import useAiWidget from './hooks/useAiWidget';
import useWidgetEntrance from './hooks/useWidgetEntrance';
import { RING_MASK_CLASSES, type AiWidgetDirection } from './constants';

interface Props {
  prompt: string;
  label: string;
  isActive?: boolean;
  direction?: AiWidgetDirection;
  labelClassName?: string;
  className?: string;
  onShowOptions?: () => void;
  onOptionClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onLoad?: () => void;
}

export default function AiSummaryWidget({
  prompt,
  label,
  isActive = true,
  direction = 'toTop',
  className,
  labelClassName,
  onShowOptions,
  onOptionClick,
  onLoad,
}: Props) {
  const panelId = useId();
  const titleId = useId();

  const {
    isOpen,
    isExpanded,
    reduceMotion,
    widgetRef,
    fabRef,
    firstRowRef,
    toggleOpen,
    closeAndFocus,
    handleMouseEnter,
    handleMouseLeave,
    handleWidgetMouseLeave,
    handleFocus,
  } = useAiWidget({ isActive, onShowOptions });

  const isEntering = useWidgetEntrance({ isActive, reduceMotion, onLoad });

  return (
    <motion.div
      ref={widgetRef}
      className={twMerge('relative inline-block', className)}
      initial={reduceMotion ? false : { scale: 0, rotate: -90, opacity: 0 }}
      animate={
        isActive || reduceMotion ? { scale: 1, rotate: 0, opacity: 1 } : { scale: 0, rotate: -90, opacity: 0 }
      }
      transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
      onMouseLeave={handleWidgetMouseLeave}
    >
      <button
        ref={fabRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={label}
        onClick={toggleOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleMouseLeave}
        className={twMerge(
          'shadow-lv3 from-site-neutrals-600 to-site-neutrals-900 focus-visible:outline-site-primary-600 group relative isolate grid h-12 min-w-12 items-center rounded-xl bg-gradient-to-l p-3 text-left outline-none transition-[grid-template-columns,transform,box-shadow,opacity] duration-300 ease-out hover:shadow-[0_8px_18px_-8px_theme(colors.site.primary.600/45%),0px_4px_8px_-2px_theme(colors.site.neutrals.900/10%),0px_2px_4px_-2px_theme(colors.site.neutrals.900/10%)] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] active:scale-[0.97] active:duration-75',
          !isOpen && 'animate-breathe hover:[animation:none]',
          {
            'grid-cols-[1.5rem_1fr]': isExpanded || isOpen,
            'grid-cols-[1.5rem_0fr]': !isExpanded && !isOpen,
            'pointer-events-none scale-[0.6] opacity-0 !duration-150': isOpen,
          },
        )}
      >
        <span aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-xl">
          <motion.span
            className="bg-site-neutrals-0/60 absolute -top-3.5 h-[6.5625rem] w-[7.5rem] rounded-full blur-[1.25rem]"
            initial={false}
            animate={{ left: isExpanded ? 'calc(100% - 1rem)' : '-7.5rem' }}
            transition={{ duration: 0.46, ease: [0.2, 0.8, 0.2, 1] }}
          />
        </span>

        <span
          aria-hidden
          className={twMerge(
            'pointer-events-none absolute -inset-0.5 z-0 overflow-hidden rounded-[0.875rem] opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100',
            RING_MASK_CLASSES,
          )}
        >
          <span className="group-hover:animate-orbit absolute left-1/2 top-1/2 aspect-square w-[200%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,theme(colors.site.primary.600),theme(colors.site.primary.200),theme(colors.site.neutrals.0),theme(colors.site.primary.200),theme(colors.site.primary.600))]" />
        </span>

        {isEntering && <WidgetEntranceEffect />}

        <span className="relative z-[2] flex size-6 items-center justify-center">
          <motion.span
            className="flex"
            animate={{ rotate: isOpen ? 90 : 0, scale: isOpen ? 1.08 : 1 }}
            transition={{ duration: 0.46, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <IconComponent iconName="AiStarCustom" size="IconM" className="bg-site-neutrals-0" optSize={48} />
          </motion.span>
        </span>
        <span className="relative z-[2] overflow-hidden whitespace-nowrap">
          <span
            className={twMerge(
              'site-paragraph-s text-site-neutrals-0 block py-0 pl-2 pr-1 font-medium',
              labelClassName,
            )}
          >
            {label}
          </span>
        </span>
      </button>

      <AiWidgetPanel
        id={panelId}
        titleId={titleId}
        label={label}
        prompt={prompt}
        direction={direction}
        isOpen={isOpen}
        onClose={closeAndFocus}
        onOptionClick={onOptionClick}
        firstRowRef={firstRowRef}
      />
    </motion.div>
  );
}
